import { makeStyles, mergeClasses } from '@fluentui/react-components';
import * as React from 'react';
import { useSelector } from 'react-redux';
import ActionBarContainer from '../components/action-bar/action-bar';
import { ProjectFilterContextProvider } from '../components/action-bar/project-picker/contexts';
import { ResourceFilterContextProvider } from '../components/action-bar/resource-picker/contexts';
import AddDevBoxPanel from '../components/add-dev-box-panel/add-dev-box-panel';
import { AddDevBoxPanelContextProvider } from '../components/add-dev-box-panel/contexts';
import AddEnvironmentPanel from '../components/add-environment-panel/add-environment-panel';
import { AddEnvironmentPanelContextProvider } from '../components/add-environment-panel/contexts';
import ConfirmationDialogContextWrapper from '../components/common/confirmation-dialog/confirmation-dialog';
import { ConfirmationDialogContextProvider } from '../components/common/confirmation-dialog/contexts';
import Footer from '../components/common/footer';
import { CustomizationDetailsPanelContextProvider } from '../components/customization-details-panel/contexts';
import CustomizationDetailsPanel from '../components/customization-details-panel/customization-details-panel';
import { ConfigureConnectionDialogContextProvider } from '../components/dev-box/configure-connection-dialog/contexts';
import { DelayShutdownDialogContextProvider } from '../components/dev-box/delay-shutdown-dialog/contexts';
import DelayShutdownDialogContextWrapper from '../components/dev-box/delay-shutdown-dialog/delay-shutdown-dialog';
import { DevBoxDetailsPanelContextProvider } from '../components/dev-box/dev-box-details-panel/context';
import { DevBoxDetailsPanelContainer } from '../components/dev-box/dev-box-details-panel/dev-box-details-panel';
import { DevBoxSupportPanelContextProvider } from '../components/dev-box/dev-box-support-panel/context';
import { DevBoxSupportPanelContextWrapper } from '../components/dev-box/dev-box-support-panel/dev-box-support-panel';
import { KeepDevBoxContextProvider } from '../components/dev-box/keep-devbox-dialog/context';
import { KeepDevBoxDialogContextWrapper } from '../components/dev-box/keep-devbox-dialog/keep-dev-box-dialog';
import { RepairDialogContextProvider } from '../components/dev-box/repair-dialog/context';
import { RepairDialogContextWrapper } from '../components/dev-box/repair-dialog/repair-dialog';
import { RestoreSnapshotDialogContextProvider } from '../components/dev-box/restore-snapshot-dialog/contexts';
import RestoreSnapshotDialogContextWrapper from '../components/dev-box/restore-snapshot-dialog/restore-snapshot-dialog';
import { TakeSnapshotDialogContextProvider } from '../components/dev-box/take-snapshot/context';
import { TakeSnapshotDialogContextWrapper } from '../components/dev-box/take-snapshot/take-snapshot-dialog';
import { DevMenuPanelContextProvider } from '../components/dev-menu-panel/contexts';
import DevMenuPanel from '../components/dev-menu-panel/dev-menu-panel';
import { ChangeEnvironmentExpirationDialogContextWrapper } from '../components/environment/change-expiration-dialog/change-environment-expiration-dialog';
import { ChangeEnvironmentExpirationDialogContextProvider } from '../components/environment/change-expiration-dialog/contexts';
import { DeploymentLogsPanelContextProvider } from '../components/environment/deployment-logs-panel/context';
import DeploymentLogsPanelWrapper from '../components/environment/deployment-logs-panel/deployment-logs-panel';
import { EnvironmentDetailsPanelContextProvider } from '../components/environment/environment-details-panel/context';
import EnvironmentDetailsPanelContainer from '../components/environment/environment-details-panel/environment-details-panel';
import { ForceDeleteDialogContextProvider } from '../components/environment/force-delete-dialog/contexts';
import { ForceDeleteDialogContextWrapper } from '../components/environment/force-delete-dialog/force-delete-dialog';
import { RedeployEnvironmentDialogContextProvider } from '../components/environment/redeploy-environment-dialog/contexts';
import { RedeployEnvironmentDialogContextWrapper } from '../components/environment/redeploy-environment-dialog/redeploy-environment-dialog';
import { ErrorDetailsPanelContextProvider } from '../components/error-details-panel/contexts';
import ErrorDetailsPanel from '../components/error-details-panel/error-details-panel';
import { HelpMenuPanelContextProvider } from '../components/help-menu-panel/contexts';
import HelpMenuPanel from '../components/help-menu-panel/help-menu-panel';
import WhenSignedIn from '../components/identity/when-signed-in';
import Information from '../components/information/information';
import { ConnectWithVSCodeDialogContextWrapper } from '../components/quick-action-dialogs/connect-with-vscode-dialog/connect-with-vscode-dialog';
import { ConnectWithVSCodeDialogContextProvider } from '../components/quick-action-dialogs/connect-with-vscode-dialog/connect-with-vscode-dialog-context';
import { McpPromotionDialogContextWrapper } from '../components/quick-action-dialogs/mcp-promotion-dialog/mcp-promotion-dialog';
import { McpPromotionDialogContextProvider } from '../components/quick-action-dialogs/mcp-promotion-dialog/mcp-promotion-dialog-context';
import { RedeployEnvironmentPanelContextProvider } from '../components/redeploy-environment-panel/contexts';
import RedeployEnvironmentPanel from '../components/redeploy-environment-panel/redeploy-environment-panel';
import TitleBar from '../components/title-bar/title-bar';
import { ErrorToast } from '../components/toast/components/error-toast';
import { InProgressToast } from '../components/toast/components/in-progress-toast';
import { InfoToast } from '../components/toast/components/info-toast';
import { SuccessToast } from '../components/toast/components/success-toast';
import { WarningToast } from '../components/toast/components/warning-toast';
import {
    ErrorToastContextProvider,
    InProgressToastContextProvider,
    InfoToastContextProvider,
    SuccessToastContextProvider,
    WarningToastContextProvider,
} from '../components/toast/contexts/toast-contexts';
import { PortalContainerProvider } from '../components/toast/portal-provider';
import ConnectViaAppDialogContextWrapper from '../components/user-settings/connect-via-app-dialog/connect-via-app-dialog';
import { ConnectViaAppDialogContextProvider } from '../components/user-settings/connect-via-app-dialog/contexts';
import { UserSettingsPanelContextProvider } from '../components/user-settings/user-settings-menu-panel/contexts';
import UserSettingsPanel from '../components/user-settings/user-settings-menu-panel/user-settings-panel';
import { getThemeModeSelector } from '../redux/selector/user-settings-selector';
import { useStackStyles } from '../themes/styles/flexbox-styles';
import { ThemeMode } from '../themes/themes';
import { PropsWithChildren } from '../types/props-with-children';

type Props = PropsWithChildren<{
    id: string;
    showFooter?: boolean;
    hideActionBar?: boolean;
    isScrolled?: boolean;
}>;

export type PortalLayoutProps = Props;

/**
 * Styles
 */

const useLayoutStyles = makeStyles({
    container: {
        position: 'relative',
        top: 0,
        bottom: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        display: 'flex',
        flexDirection: 'column',
        flexGrow: 1,
        colorScheme: 'dark',
    },
    content: {
        flexGrow: 1,
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
    },
    header: {
        height: 'fit-content',
        width: '100vw',
    },
    body: {
        position: 'relative',
        top: 0,
        bottom: 0,
        left: 0,
        overflowY: 'auto',
        overflowX: 'auto',
        flexGrow: 1,
        flexBasis: 0,
        minHeight: 0,
        flexDirection: 'column',
        height: '100%',
        justifyContent: 'space-between',
    },
    main: {
        minWidth: '100%',
        alignSelf: 'start',
        flexGrow: '1',
    },
    footer: {
        minWidth: '100vw',
        alignSelf: 'end',
    },
});

const useLightStyles = makeStyles({
    container: {
        colorScheme: 'light',
    },
});

/**
 * End Styles
 */

const PanelsContextProvider: React.FC<PropsWithChildren> = (props: PropsWithChildren) => {
    const { children } = props;

    return (
        <AddDevBoxPanelContextProvider>
            <AddEnvironmentPanelContextProvider>
                <DevMenuPanelContextProvider>
                    <UserSettingsPanelContextProvider>
                        <ErrorDetailsPanelContextProvider>
                            <HelpMenuPanelContextProvider>
                                <DevBoxDetailsPanelContextProvider>
                                    <RedeployEnvironmentPanelContextProvider>
                                        <CustomizationDetailsPanelContextProvider>
                                            <EnvironmentDetailsPanelContextProvider>
                                                <DevBoxSupportPanelContextProvider>
                                                    <DeploymentLogsPanelContextProvider>
                                                        {children}
                                                    </DeploymentLogsPanelContextProvider>
                                                </DevBoxSupportPanelContextProvider>
                                            </EnvironmentDetailsPanelContextProvider>
                                        </CustomizationDetailsPanelContextProvider>
                                    </RedeployEnvironmentPanelContextProvider>
                                </DevBoxDetailsPanelContextProvider>
                            </HelpMenuPanelContextProvider>
                        </ErrorDetailsPanelContextProvider>
                    </UserSettingsPanelContextProvider>
                </DevMenuPanelContextProvider>
            </AddEnvironmentPanelContextProvider>
        </AddDevBoxPanelContextProvider>
    );
};

const DialogsContextProvider: React.FC<PropsWithChildren> = (props: PropsWithChildren) => {
    const { children } = props;

    return (
        <ConnectViaAppDialogContextProvider>
            <ConfigureConnectionDialogContextProvider>
                <DelayShutdownDialogContextProvider>
                    <ConfirmationDialogContextProvider>
                        <RedeployEnvironmentDialogContextProvider>
                            <RepairDialogContextProvider>
                                <TakeSnapshotDialogContextProvider>
                                    <ChangeEnvironmentExpirationDialogContextProvider>
                                        <RestoreSnapshotDialogContextProvider>
                                            <ForceDeleteDialogContextProvider>
                                                <McpPromotionDialogContextProvider>
                                                    <ConnectWithVSCodeDialogContextProvider>
                                                        <KeepDevBoxContextProvider>
                                                            {children}
                                                        </KeepDevBoxContextProvider>
                                                    </ConnectWithVSCodeDialogContextProvider>
                                                </McpPromotionDialogContextProvider>
                                            </ForceDeleteDialogContextProvider>
                                        </RestoreSnapshotDialogContextProvider>
                                    </ChangeEnvironmentExpirationDialogContextProvider>
                                </TakeSnapshotDialogContextProvider>
                            </RepairDialogContextProvider>
                        </RedeployEnvironmentDialogContextProvider>
                    </ConfirmationDialogContextProvider>
                </DelayShutdownDialogContextProvider>
            </ConfigureConnectionDialogContextProvider>
        </ConnectViaAppDialogContextProvider>
    );
};

const ToastsContextProvider: React.FC<PropsWithChildren> = (props: PropsWithChildren) => {
    const { children } = props;

    return (
        <InfoToastContextProvider>
            <SuccessToastContextProvider>
                <ErrorToastContextProvider>
                    <WarningToastContextProvider>
                        <InProgressToastContextProvider>{children}</InProgressToastContextProvider>
                    </WarningToastContextProvider>
                </ErrorToastContextProvider>
            </SuccessToastContextProvider>
        </InfoToastContextProvider>
    );
};

const SurfacesProvider: React.FC<PropsWithChildren> = (props: PropsWithChildren) => {
    const { children } = props;

    return (
        <ToastsContextProvider>
            <PanelsContextProvider>
                <DialogsContextProvider>
                    <AddDevBoxPanel />
                    <AddEnvironmentPanel />
                    <DevMenuPanel />
                    <UserSettingsPanel />
                    <ErrorDetailsPanel />
                    <CustomizationDetailsPanel />
                    <HelpMenuPanel />
                    <DevBoxDetailsPanelContainer />
                    <DevBoxSupportPanelContextWrapper />
                    <RedeployEnvironmentPanel />
                    <DelayShutdownDialogContextWrapper />
                    <ConfirmationDialogContextWrapper />
                    <RedeployEnvironmentDialogContextWrapper />
                    <ChangeEnvironmentExpirationDialogContextWrapper />
                    <ForceDeleteDialogContextWrapper />
                    <TakeSnapshotDialogContextWrapper />
                    <RepairDialogContextWrapper />
                    <KeepDevBoxDialogContextWrapper />
                    <McpPromotionDialogContextWrapper />
                    <ConnectWithVSCodeDialogContextWrapper />
                    <EnvironmentDetailsPanelContainer />
                    <ConnectViaAppDialogContextWrapper />
                    <RestoreSnapshotDialogContextWrapper />
                    <DeploymentLogsPanelWrapper />
                    <InfoToast />
                    <SuccessToast />
                    <ErrorToast />
                    <WarningToast />
                    <InProgressToast />
                    {children}
                </DialogsContextProvider>
            </PanelsContextProvider>
        </ToastsContextProvider>
    );
};

const FiltersContextProvider: React.FC<PropsWithChildren> = (props: PropsWithChildren) => {
    const { children } = props;

    return (
        <ResourceFilterContextProvider>
            <ProjectFilterContextProvider>{children}</ProjectFilterContextProvider>
        </ResourceFilterContextProvider>
    );
};

const FiltersProvider: React.FC<PropsWithChildren> = (props: PropsWithChildren) => {
    const { children } = props;

    return <FiltersContextProvider>{children}</FiltersContextProvider>;
};

export const PortalLayout: React.FC<Props> = (props) => {
    const { children, id, showFooter, hideActionBar, isScrolled } = props;

    const currentThemeMode = useSelector(getThemeModeSelector);

    // Style hooks
    const styles = useLayoutStyles();
    const stackStyles = useStackStyles();
    const lightStyles = useLightStyles();

    const containerStyles = React.useMemo(
        () =>
            currentThemeMode === ThemeMode.LightMode
                ? mergeClasses(styles.container, lightStyles.container)
                : styles.container,
        [currentThemeMode, styles.container, lightStyles.container]
    );

    return (
        <PortalContainerProvider>
            <SurfacesProvider>
                <FiltersProvider>
                    <div id="portal-layout-container" className={containerStyles}>
                        <div id="portal-layout-content" className={styles.content}>
                            <div id="portal-layout-header" className={styles.header}>
                                <TitleBar isScrolled={isScrolled} />

                                <WhenSignedIn>
                                    {!hideActionBar && (
                                        <>
                                            <ActionBarContainer />
                                            <Information />
                                        </>
                                    )}
                                </WhenSignedIn>
                            </div>
                            <div id="portal-layout-body" className={mergeClasses(stackStyles.root, styles.body)}>
                                <div id={id} role="main" className={mergeClasses(stackStyles.item, styles.main)}>
                                    {children}
                                </div>
                                {showFooter && (
                                    <div className={mergeClasses(stackStyles.item, styles.footer)}>
                                        <Footer />
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </FiltersProvider>
            </SurfacesProvider>
        </PortalContainerProvider>
    );
};
